/**
 * generateAndStoreColorGrids.js
 * ----------------------------------------------------------
 * Creates three random 6x6 color grids using Red, Yellow, Blue, and Green.
 * Each grid is stored as a separate document inside:
 * db / TrustIssuesRoomCollection / roomID / ColorGrids
 *
 * Example Firestore structure:
 * TrustIssuesRoomCollection
 *  └── roomID
 *       └── ColorGrids
 *            ├── grid1 → { grid: [["Red", "Blue", ...], ...] }
 *            ├── grid2 → { grid: [["Green", "Yellow", ...], ...] }
 *            └── grid3 → { grid: [["Blue", "Red", ...], ...] }
 */

import { db } from "../../../config/firebase";
import { arrayUnion, collection, doc, getDoc, getDocs, onSnapshot, setDoc, Timestamp, updateDoc } from "firebase/firestore";
import { ColorGrids, TrustIssuesRoomCollection } from "../../../assets/DatabaseCollections";
import { getNumberOfImpostors } from "../assets/Details";

/**
 * Fetches all color grids from Firestore for the given room.
 */
export const fetchColorGrids = async (roomID) => {
    try {
        const gridsRef = collection(db, TrustIssuesRoomCollection, roomID, ColorGrids);
        const snap = await getDocs(gridsRef);
        return snap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    } catch (err) {
        console.error("[ColorCoding] Error fetching color grids:", err);
        return [];
    }
};

/**
 * Shuffles an array using the Fisher-Yates algorithm.
 */
const shuffleArray = (array) => {
    const arr = [...array];
    for (let i = arr.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [arr[i], arr[j]] = [arr[j], arr[i]];
    }
    return arr;
};

/**
 * Generates one random 6x6 grid flattened into a single array (length = 36).
 * @returns {string[]} Array of 36 colors.
 */
const generateRandomGrid = () => {
    const colors = ["Red", "Yellow", "Blue", "Green"];
    const colorPool = [];
    for (let i = 0; i < 36; i++) {
        colorPool.push(colors[i % colors.length]);
    }
    return shuffleArray(colorPool);
};

/**
 * Generates three color grids and writes them as separate docs in Firestore.
 * @param {string} roomID - The game room ID.
 */
export const generateAndStoreColorGrids = async (roomID, totalPlayers) => {
    try {
        if (!roomID) throw new Error("Room ID is required to generate color grids.");

        const gridsCollectionRef = collection(
            db,
            TrustIssuesRoomCollection,
            roomID,
            ColorGrids
        );

        // Generate 3 flat grids
        const numberOfGrids = getNumberOfImpostors(totalPlayers) - 1
        for (let i = 0; i < numberOfGrids; i++) {
            const gridRef = doc(gridsCollectionRef, `grid${i + 1}`);
            const randomGrid = generateRandomGrid();

            await setDoc(gridRef, {
                grid: randomGrid.flat(), // flatten 2D -> 1D for Firestore
                createdAt: Date.now(),
                opened : false
            });
        }
    } catch (error) {
        console.error("[ColorGrids] Error generating grids:", error);
    }
};

/**
 * Assigns an unassigned grid to a user.
 * Updates:
 *  - room.assignedGrids = array of { userID, gridID, finishesAt }
 *  - ColorGrids/gridX.assignedTo = { id, name }
 *  - ColorGrids/gridX.opened = true, finishedAt = now + 45 s
 */
export const assignMeAGrid = async (user, roomID) => {
    try {
        const roomRef = doc(db, TrustIssuesRoomCollection, roomID);
        const gridsRef = collection(roomRef, "ColorGrids");
        const gridDocs = await getDocs(gridsRef);

        // Find first grid without assignedTo
        let targetDoc = null;
        gridDocs.forEach((d) => {
            const data = d.data();
            if (!data.assignedTo && !targetDoc) targetDoc = { id: d.id, ...data };
        });

        if (!targetDoc) return null; // all taken

        // Assign to user
        const finishesAt = Date.now() + 45 * 1000;
        const gridRef = doc(gridsRef, targetDoc.id);

        await updateDoc(gridRef, {
            assignedTo: { id: user.uid, name: user.displayName || "Player" },
            opened: true,
            finishedAt: Timestamp.fromMillis(finishesAt),
        });

        // Optionally track in room doc
        const roomSnap = await getDoc(roomRef);
        const current = roomSnap.data()?.assignedGrids || [];
        await updateDoc(roomRef, {
            assignedGrids: [
                ...current,
                { userID: user.uid, gridID: targetDoc.id, finishesAt },
            ],
        });

        return { grid: { ...targetDoc, id: targetDoc.id }, finishesAt };
    } catch (err) {
        console.error("[assignMeAGrid] Failed to assign grid:", err);
        return null;
    }
};

/**
 * Submit the user's answer grid and calculate score.
 * Updates Firestore doc: answers[userID] & scores[userID]
 */
export const submitAnswerGrid = async (roomID, gridID, userID, answers) => {
    try {
        const gridRef = doc(db, TrustIssuesRoomCollection, roomID, "ColorGrids", gridID);
        const snap = await getDoc(gridRef);
        const data = snap.data();
        const original = data.grid || [];

        let correct = 0;
        for (let i = 0; i < 36; i++) {
            if (answers[i] && answers[i] === original[i]) correct++;
        }
        const accuracy = Math.round((correct / 36) * 100);

        await updateDoc(gridRef, {
            answer : answers,
            score : accuracy,
        });
        return accuracy;
    } catch (err) {
        console.error("[submitAnswerGrid] Failed:", err);
        return 0;
    }
};

/**
 * Marks user as ready for next stage.
 */
export const markReadyToProceed = async (roomID, userID) => {
    try {
        const roomRef = doc(db, TrustIssuesRoomCollection, roomID);
        await updateDoc(roomRef, {
            ReadyClicksColorCoding: arrayUnion(userID),
        });
    } catch (err) {
        console.error("[markReadyToProceed] Error:", err);
    }
};

/**
 * Listens for real-time updates to a ColorGrid doc.
 * When all assigned users have submitted their answers,
 * triggers the callback with the updated data.
 *
 * @param {string} roomID      - Firestore room ID.
 * @param {string} gridID      - The grid document ID (grid1, grid2, etc.)
 * @param {string[]} members   - Array of userIDs assigned to this grid.
 * @param {Function} callback  - Function to call when all answers are in.
 * @returns {Function} - unsubscribe function
 */
export const listenForAllAnswers = (roomID, gridID, members, callback) => {
    try {
        const gridRef = doc(db, TrustIssuesRoomCollection, roomID, "ColorGrids", gridID);

        const unsub = onSnapshot(gridRef, (snap) => {
            if (!snap.exists()) return;
            const data = snap.data();

            // Check how many players have submitted
            const submitted = Object.keys(data.answers || {});
            const totalRequired = members.length;

            // If everyone has submitted → trigger result phase
            if (submitted.length >= totalRequired) {
                callback(data);
            }
        });

        return unsub;
    } catch (error) {
        console.error("[listenForAllAnswers] Error setting listener:", error);
        return () => {};
    }
};